
import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Download, Palette, Type, Sparkles, Coins, Wand2, RefreshCw, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useCredits } from '@/hooks/useCredits';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';
import { trackCoverGeneration, trackDownload } from '@/lib/analytics';
import { TemplateRenderer, RenderOptions } from '@/lib/templateRenderer';
import { HTMLToImageConverter } from '@/lib/htmlToImage';
import { companyThemes } from '@/lib/templates/companyThemes';
import { htmlTemplates } from '@/lib/templates/htmlTemplates';
import TemplateSelector from './TemplateSelector';

// Environment flag to enable/disable authentication
const USE_MOCK_AUTH = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

export interface GenerationMode {
  id: 'default' | 'ai';
  name: string;
  description: string;
  icon: React.ReactNode;
}

const generationModes: GenerationMode[] = [
  {
    id: 'default',
    name: '爆款模板',
    description: '使用精选的病毒传播模板',
    icon: <Palette className="w-4 h-4" />
  },
  {
    id: 'ai',
    name: 'AI 杂志风',
    description: '杂志级别的 AI 定制设计',
    icon: <Wand2 className="w-4 h-4" />
  }
];

const CoverGenerator = () => {
  const { user } = useAuth();
  const { credits, decrementCredits, refreshCredits } = useCredits();
  const [titleText, setTitleText] = useState('分享生活的美好时光');
  const [subtitleText, setSubtitleText] = useState('记录每个精彩瞬间');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [generationMode, setGenerationMode] = useState<'default' | 'ai'>('default');
  const [selectedTemplate, setSelectedTemplate] = useState(htmlTemplates[0].id);
  const [selectedCompany, setSelectedCompany] = useState('random');
  const [aiStyle, setAiStyle] = useState('');
  const [aiTheme, setAiTheme] = useState('');
  const [currentHtml, setCurrentHtml] = useState('');
  const [currentTemplateId, setCurrentTemplateId] = useState('');
  const [currentCompanyName, setCurrentCompanyName] = useState('');
  const previewRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);


  // Generate cover using the new template system
  const handleGenerate = async () => {
    if (!titleText.trim() || !subtitleText.trim()) {
      toast.error('请输入标题和副标题');
      return;
    }

    setIsGenerating(true);
    try {
      const options: RenderOptions = {
        useAI: generationMode === 'ai',
        templateId: generationMode === 'default' ? selectedTemplate : undefined,
        companyName: generationMode === 'default' && selectedCompany !== 'random' ? selectedCompany : undefined,
        style: generationMode === 'ai' ? aiStyle : undefined,
        theme: generationMode === 'ai' ? aiTheme : undefined
      };

      const result = await TemplateRenderer.generateCover(titleText, subtitleText, options);

      if (result.success) {
        setCurrentHtml(result.html);
        setCurrentTemplateId(result.templateId);
        setCurrentCompanyName(result.companyName);

        // HTML will be rendered by the JSX iframe below

        if (result.error) {
          toast.warning(result.error); // Show fallback message
        } else {
          toast.success('封面生成成功！');
        }

        // Track generation
        trackCoverGeneration(result.templateId, true);
      } else {
        toast.error(result.error || '生成失败，请重试');
        trackCoverGeneration('unknown', false);
      }
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error('生成失败，请重试');
      trackCoverGeneration('unknown', false);
    } finally {
      setIsGenerating(false);
    }
  };

  // Download the generated cover as image
  const handleDownload = async () => {
    if (!currentHtml) {
      toast.error('请先生成封面');
      return;
    }

    if (!USE_MOCK_AUTH) {
      if (!user) {
        toast.error('请先登录');
        return;
      }

      if (credits < 1) {
        toast.error('积分不足，请先充值');
        return;
      }
    }

    setIsDownloading(true);
    try {
      // Skip credit deduction in mock mode
      if (!USE_MOCK_AUTH) {
        // 先获取当前用户数据并扣减积分
        const { data: userData, error: fetchError } = await supabase
          .from('users')
          .select('remaining_credits, total_usage')
          .eq('id', user.id)
          .single();

        if (fetchError || !userData) {
          console.error('获取用户数据失败:', fetchError);
          toast.error('获取用户数据失败，请重试');
          return;
        }

        const { error: creditError } = await supabase
          .from('users')
          .update({
            remaining_credits: userData.remaining_credits - 1,
            total_usage: (userData.total_usage || 0) + 1
          })
          .eq('id', user.id);

        if (creditError) {
          console.error('扣减积分失败:', creditError);
          toast.error('积分扣减失败，请重试');
          return;
        }

        // 记录积分使用日志
        await supabase.from('credit_usage').insert({
          user_id: user.id,
          action_type: 'download_cover',
          credits_used: 1,
          metadata: {
            template_id: currentTemplateId,
            company_name: currentCompanyName,
            generation_mode: generationMode
          }
        });
      }

      // Convert HTML to image
      const result = await HTMLToImageConverter.convertHTMLToImage(currentHtml, {
        width: 400,
        height: 600,
        scale: 2,
        format: 'png'
      });

      if (result.success && result.blob) {
        HTMLToImageConverter.downloadImage(result.blob, '小红书封面.png');

        // 更新本地积分显示 (only in non-mock mode)
        if (!USE_MOCK_AUTH) {
          decrementCredits(1);
        }

        // 追踪下载事件
        trackDownload('cover_image', 'cover_generator');

        const successMessage = USE_MOCK_AUTH ? '封面图已下载！' : '封面图已下载！消耗 1 积分';
        toast.success(successMessage);
      } else {
        throw new Error(result.error || '图片转换失败');
      }
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('下载失败，请重试');
    } finally {
      setIsDownloading(false);
    }
  };

  // Copy HTML to clipboard
  const handleCopyHtml = async () => {
    if (!currentHtml) {
      toast.error('请先生成封面');
      return;
    }

    try {
      await navigator.clipboard.writeText(currentHtml);
      toast.success('HTML 代码已复制到剪贴板');
    } catch (error) {
      toast.error('复制失败，请重试');
    }
  };

  // Generate initial cover on component mount
  useEffect(() => {
    handleGenerate();
  }, []);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left Panel - Controls */}
        <div className="space-y-6">
          {/* Text Content */}
          <Card className="xiaohongshu-shadow border-0">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Type className="w-5 h-5 text-xiaohongshu-red" />
                <Label className="text-lg font-semibold">文字内容</Label>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="title" className="text-sm font-medium text-gray-700 mb-2 block">
                    主标题
                  </Label>
                  <Textarea
                    id="title"
                    placeholder="输入主标题文字..."
                    value={titleText}
                    onChange={(e) => setTitleText(e.target.value)}
                    className="min-h-[60px] resize-none border-gray-200 focus:border-xiaohongshu-red focus:ring-xiaohongshu-red"
                  />
                </div>

                <div>
                  <Label htmlFor="subtitle" className="text-sm font-medium text-gray-700 mb-2 block">
                    副标题
                  </Label>
                  <Textarea
                    id="subtitle"
                    placeholder="输入副标题文字..."
                    value={subtitleText}
                    onChange={(e) => setSubtitleText(e.target.value)}
                    className="min-h-[60px] resize-none border-gray-200 focus:border-xiaohongshu-red focus:ring-xiaohongshu-red"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Generation Mode */}
          <Card className="xiaohongshu-shadow border-0">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Wand2 className="w-5 h-5 text-xiaohongshu-red" />
                <Label className="text-lg font-semibold">生成模式</Label>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                {generationModes.map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => setGenerationMode(mode.id)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      generationMode === mode.id
                        ? 'border-xiaohongshu-red bg-xiaohongshu-red/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {mode.icon}
                      <span className="font-medium text-sm">{mode.name}</span>
                    </div>
                    <p className="text-xs text-gray-600">{mode.description}</p>
                  </button>
                ))}
              </div>

              {/* Default Template Options */}
              {generationMode === 'default' && (
                <div className="space-y-6">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-3 block">
                      选择爆款样式
                    </Label>
                    <TemplateSelector
                      templates={htmlTemplates}
                      selectedTemplate={selectedTemplate}
                      onTemplateSelect={setSelectedTemplate}
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      品牌风格
                    </Label>
                    <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                      <SelectTrigger>
                        <SelectValue placeholder="选择品牌风格" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="random">随机风格</SelectItem>
                        {companyThemes.map((theme) => (
                          <SelectItem key={theme.name} value={theme.name}>
                            {theme.logo} {theme.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* AI Generation Options */}
              {generationMode === 'ai' && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="ai-style" className="text-sm font-medium text-gray-700 mb-2 block">
                      杂志风格 (可选)
                    </Label>
                    <Textarea
                      id="ai-style"
                      placeholder="例如：极简主义、重力分层、色彩呼吸..."
                      value={aiStyle}
                      onChange={(e) => setAiStyle(e.target.value)}
                      className="min-h-[60px] resize-none border-gray-200 focus:border-xiaohongshu-red focus:ring-xiaohongshu-red"
                    />
                  </div>

                  <div>
                    <Label htmlFor="ai-theme" className="text-sm font-medium text-gray-700 mb-2 block">
                      内容主题 (可选)
                    </Label>
                    <Textarea
                      id="ai-theme"
                      placeholder="例如：爆款内容、病毒传播、热门话题..."
                      value={aiTheme}
                      onChange={(e) => setAiTheme(e.target.value)}
                      className="min-h-[60px] resize-none border-gray-200 focus:border-xiaohongshu-red focus:ring-xiaohongshu-red"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={isGenerating || !titleText.trim() || !subtitleText.trim()}
            className="w-full h-12 bg-xiaohongshu-gradient hover:opacity-90 text-white font-medium text-lg shadow-lg hover:shadow-xl transition-all duration-300"
          >
            {isGenerating ? (
              <>
                <div className="w-5 h-5 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                生成中...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                生成封面
              </>
            )}
          </Button>

          {/* Action Buttons */}
          {currentHtml && (
            <div className="space-y-3">
              {/* 积分显示 - 只在非mock模式下显示 */}
              {!USE_MOCK_AUTH && user && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Coins className="w-4 h-4 text-xiaohongshu-red" />
                    <span className="text-sm text-gray-600">当前积分:</span>
                    <span className="font-semibold text-xiaohongshu-red">{credits}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    下载消耗 1 积分
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {/* Copy HTML Button */}
                <Button
                  onClick={handleCopyHtml}
                  variant="outline"
                  className="h-10 border-xiaohongshu-red text-xiaohongshu-red hover:bg-xiaohongshu-red hover:text-white"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  复制代码
                </Button>

                {/* Download Button */}
                {USE_MOCK_AUTH ? (
                  <Button
                    onClick={handleDownload}
                    disabled={isDownloading}
                    className="h-10 bg-xiaohongshu-gradient hover:opacity-90 text-white font-medium"
                  >
                    {isDownloading ? (
                      <>
                        <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        下载中...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        下载图片
                      </>
                    )}
                  </Button>
                ) : (
                  user ? (
                    credits >= 1 ? (
                      <Button
                        onClick={handleDownload}
                        disabled={isDownloading}
                        className="h-10 bg-xiaohongshu-gradient hover:opacity-90 text-white font-medium"
                      >
                        {isDownloading ? (
                          <>
                            <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            下载中...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            下载图片
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button asChild className="h-10 bg-xiaohongshu-gradient hover:opacity-90 text-white font-medium">
                        <Link to="/credits">
                          <Coins className="w-4 h-4 mr-2" />
                          充值
                        </Link>
                      </Button>
                    )
                  ) : (
                    <Button asChild className="h-10 bg-xiaohongshu-gradient hover:opacity-90 text-white font-medium">
                      <Link to="/auth">
                        登录下载
                      </Link>
                    </Button>
                  )
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Preview */}
        <div className="lg:sticky lg:top-8">
          <Card className="xiaohongshu-shadow border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-lg">👀</span>
                  <Label className="text-lg font-semibold">实时预览</Label>
                </div>
                {currentTemplateId && (
                  <div className="text-xs text-gray-500">
                    {currentCompanyName} · {currentTemplateId}
                  </div>
                )}
              </div>

              <div
                ref={previewRef}
                className="w-full h-[600px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center"
              >
                {currentHtml ? (
                  <div className="relative">
                    <iframe
                      ref={iframeRef}
                      srcDoc={currentHtml}
                      className="w-[400px] h-[600px] border-2 border-gray-300 bg-white rounded-lg shadow-lg"
                      title="Cover Preview"
                    />
                    <div className="absolute -top-3 -left-3 bg-blue-500 text-white text-xs px-2 py-1 rounded-full shadow-md">
                      封面预览
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">选择模板并点击"生成封面"查看预览</p>
                  </div>
                )}
              </div>

              {currentHtml && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-600 mb-2">生成信息:</div>
                  <div className="text-xs space-y-1">
                    <div>模式: {generationMode === 'ai' ? 'AI 生成' : '默认模板'}</div>
                    <div>模板: {currentTemplateId}</div>
                    <div>主题: {currentCompanyName}</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CoverGenerator;
